import {
  AdminDetailGrid,
  AdminHeaderActionButton,
  AdminPageHeader,
} from '@/components/patterns/admin-page'
import OrganizationForm from '@/components/forms/OrganizationForm'
import OrganizationBetaFeaturesSection from '@/components/organizations/OrganizationBetaFeaturesSection'
import OrganizationSalesDemoSection from '@/components/organizations/OrganizationSalesDemoSection'
import { FloristLifecycleDialog } from '@/components/organizations/FloristLifecycleDialog'
import { FloristStatusBadge } from '@/components/organizations/FloristStatusBadge'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { useOrganizationContext } from '@/hooks/useOrganizationContext'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can, useAbility } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { OrganizationType } from '@/lib/enums/OrganizationType'
import { resolveOrganizationSwitchPath } from '@/lib/organization-switch'
import { organizationSchema } from '@/lib/services/organizations/organization.schema'
import { canEditOrganization } from '@/lib/services/organizations/organizations.access'
import {
  organizationQueries,
  useInvalidateStorefrontCacheMutation,
  useOrganization,
} from '@/lib/services/organizations/organizations.query'
import { useSalesDemo } from '@/lib/services/sales-demos/sales-demos.query'
import { createFileRoute, Link, useLocation } from '@tanstack/react-router'
import { BadgeCheck, Building2, CircleOff, Drama } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/_authenticated/$orgId/organizations/$organizationId/')({
  component: OrganizationDetailPage,
  params: {
    parse: (params) => organizationSchema.pick({ organizationId: true }).parse(params),
  },
  loader: async ({ context, params }) => {
    const organization = await context.queryClient.ensureQueryData(
      organizationQueries.show(params.organizationId)
    )

    return { isReadOnly: !canEditOrganization(context.ability, organization) }
  },
})

export function OrganizationDetailPage() {
  const { orgId, organizationId } = Route.useParams()
  const [isLifecycleDialogOpen, setIsLifecycleDialogOpen] = useState(false)
  const pathname = useLocation({
    select: (location) => location.pathname,
  })
  const { data: organizationData } = useOrganization(organizationId)
  const invalidateStorefrontCacheMutation = useInvalidateStorefrontCacheMutation()

  const ability = useAbility()
  const { isImpersonating, user } = useOrganizationContext()
  const path = usePathHelper()
  const viewedOrganizationId = organizationData?.id ?? organizationId
  const isImpersonatingThisOrg = isImpersonating && Number(orgId) === Number(viewedOrganizationId)
  const newId = isImpersonatingThisOrg ? user?.organization_id : viewedOrganizationId
  const isFlorist = organizationData?.type === OrganizationType.Florist.Value
  const isLive = organizationData?.florist_status === 'live'
  const isLost = organizationData?.florist_status === 'lost'
  const LifecycleActionIcon = isLive ? CircleOff : BadgeCheck
  const switchPath = newId
    ? resolveOrganizationSwitchPath({
        currentPathname: pathname,
        organizationId: newId,
        pathHelper: path,
      })
    : null
  const canManageBetaFeatures =
    ability.can('access', 'global') &&
    ability.can('manage', 'beta-features') &&
    (isFlorist || canEditOrganization(ability, organizationData))
  const canManageSalesDemos =
    ability.can('access', 'global') &&
    ability.can('manage', 'sales-demos') &&
    isFlorist &&
    (organizationData?.sales_demo_provisioning_enabled === true ||
      Boolean(organizationData?.sales_demo))
  const canEditViewedOrganization = canEditOrganization(ability, organizationData)
  const salesDemoQuery = useSalesDemo(viewedOrganizationId, {
    enabled: canManageSalesDemos,
  })
  const currentSalesDemo = salesDemoQuery.data
    ? salesDemoQuery.data.sales_demo
    : organizationData?.sales_demo

  const formatLifecycleTimestamp = (timestamp) =>
    timestamp
      ? dayjs.utc(timestamp).tz(organizationData.timezone).format('MMM D, YYYY [at] h:mm A')
      : null

  const handleInvalidateStorefrontCache = async () => {
    try {
      await invalidateStorefrontCacheMutation.mutateAsync(organizationId)
      toast.success('Storefront cache invalidated for this organization.')
    } catch {
      toast.error('Unable to invalidate storefront cache.')
    }
  }

  const mainContent = (
    <div className="space-y-4">
      <Can I="access" the="global">
        <details className="rounded-md border border-border/50 bg-elevated px-3 py-2">
          <summary className="cursor-pointer text-xs text-muted-foreground">
            Advanced: Storefront Cache
          </summary>
          <div className="pt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleInvalidateStorefrontCache}
              disabled={invalidateStorefrontCacheMutation.isPending}
            >
              {invalidateStorefrontCacheMutation.isPending
                ? 'Invalidating...'
                : 'Invalidate Storefront Cache'}
            </Button>
          </div>
        </details>
      </Can>
      <OrganizationForm
        initialData={organizationData}
        salesDemo={currentSalesDemo}
        className="w-full"
      />
    </div>
  )

  return (
    <div className="space-y-4">
      <AdminPageHeader
        icon={Building2}
        title={organizationData?.name ?? 'Organization'}
        badges={
          isFlorist ? (
            <>
              <FloristStatusBadge status={organizationData.florist_status} />
              {isLive && organizationData.live_at ? (
                <Typography variant="supporting">
                  Live since {formatLifecycleTimestamp(organizationData.live_at)}
                </Typography>
              ) : null}
              {isLost && organizationData.lost_at ? (
                <Typography variant="supporting">
                  Lost since {formatLifecycleTimestamp(organizationData.lost_at)}
                </Typography>
              ) : null}
              {isLost && organizationData.live_at ? (
                <Typography variant="supporting">
                  Previously live since {formatLifecycleTimestamp(organizationData.live_at)}
                </Typography>
              ) : null}
            </>
          ) : null
        }
        actions={
          <>
            {isFlorist && !currentSalesDemo ? (
              <Can I="access" the="global">
                {canEditViewedOrganization ? (
                  <AdminHeaderActionButton
                    type="button"
                    variant={isLive ? 'destructive' : 'default'}
                    onClick={() => setIsLifecycleDialogOpen(true)}
                  >
                    <LifecycleActionIcon />
                    {isLive ? 'Mark Lost' : 'Mark Live'}
                  </AdminHeaderActionButton>
                ) : null}
              </Can>
            ) : null}
            {Number(viewedOrganizationId) !== 1 && switchPath ? (
              <AdminHeaderActionButton
                asChild
                variant={isImpersonatingThisOrg ? 'secondary' : 'outline'}
              >
                <Link to={switchPath}>
                  <Drama className="mr-1" />
                  {isImpersonatingThisOrg ? 'Stop Impersonating' : 'Impersonate'}
                </Link>
              </AdminHeaderActionButton>
            ) : null}
          </>
        }
      />
      {isFlorist && !currentSalesDemo ? (
        <FloristLifecycleDialog
          organization={organizationData}
          open={isLifecycleDialogOpen}
          onOpenChange={setIsLifecycleDialogOpen}
        />
      ) : null}
      {canManageBetaFeatures || canManageSalesDemos ? (
        <AdminDetailGrid
          asideFirstOnStack
          main={mainContent}
          aside={
            <>
              {canManageSalesDemos ? (
                <OrganizationSalesDemoSection
                  organizationId={viewedOrganizationId}
                  salesDemoQuery={salesDemoQuery}
                  timezone={organizationData?.timezone}
                />
              ) : null}
              {canManageBetaFeatures ? (
                <OrganizationBetaFeaturesSection organizationId={viewedOrganizationId} />
              ) : null}
            </>
          }
        />
      ) : (
        <div className="max-w-4xl">{mainContent}</div>
      )}
    </div>
  )
}
